import { csx, Skeleton, Stack, Text, Tooltip } from '@vtex/admin-ui'
import { ImageWithFallback } from '@/components/ImageWithFallback'
import {
  useNavigateToSkuEditor,
  useSkuEditorURL,
} from '@/components/products-listing/hooks/useEditorURLs'
import {
  StatusIcon,
  useStatusIcon,
} from '@/components/products-listing/status-icon'
import { SKUQuickActions } from '@/components/SKUQuickActions'
import { useAnalytics } from '@/contexts/analytics'
import { executeClickHandlers } from '@/lib/middleware'
import { handleAnchorClickSafely } from '@/lib/navigate'
import { extractSKUNameFromNameComplete } from '@/lib/transform'
import type { Product, SKUListing } from '@/types/product'

import * as styles from './SKURow.styles'

type SKURowProps = {
  sku: SKUListing
  product: Product
}

export const SKURow = ({ sku, product }: SKURowProps) => {
  const analytics = useAnalytics()
  const skuName = extractSKUNameFromNameComplete(sku.nameComplete, product.name)
  const status = useStatusIcon({
    active: sku.isActive,
    visibleInPortal: sku.isVisibleInPortal,
  })
  const handleNavigateToSku = useNavigateToSkuEditor(product.id, sku.id)

  const skuLink = useSkuEditorURL(product.id, sku.id)

  const dispatchEventOnSkuClick = () => {
    analytics.track('Clicked on the list', {
      Action: 'Edit SKU',
    })
  }

  return (
    <a
      className={csx(styles.skuRowLinkWrapper)}
      href={skuLink}
      onAuxClick={dispatchEventOnSkuClick}
      onClick={executeClickHandlers(
        dispatchEventOnSkuClick,
        handleAnchorClickSafely(handleNavigateToSku)
      )}
      onContextMenu={dispatchEventOnSkuClick}
    >
      <tr className={csx(styles.skuRow)} role="row" key={sku.id}>
        <td role="cell" />
        <td role="cell" className={csx(styles.skuCell)}>
          <div className={csx(styles.skuWrapper)}>
            <StatusIcon {...status} />
            <ImageWithFallback
              src={sku.mainImageUrl}
              alt={skuName}
              width={24}
              height={24}
            />
            <Tooltip placement="top" text={skuName}>
              <Text
                className={`${csx(styles.skuName)} sku-title`}
                tone="secondary"
              >
                {`${sku.id} - ${skuName}`}
              </Text>
            </Tooltip>
          </div>
          <div>
            <SKUQuickActions sku={sku} product={product} />
          </div>
        </td>
        <td role="cell" />
      </tr>
    </a>
  )
}

export const SKURowSkeleton = () => (
  <tr role="row" className={csx(styles.skuRow)}>
    <td role="cell" />
    <td role="cell">
      <div className={csx(styles.skuWrapper)}>
        <Stack
          direction="row"
          className={csx({
            width: '16px',
            height: '16px',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <Skeleton
            shape="circle"
            className={csx({ height: '0.5rem', width: '0.5rem' })}
          />
        </Stack>
        <Skeleton
          className={csx(styles.skuImageContainer({ type: 'fallback' }))}
        />
        <Skeleton className={csx({ height: '0.75rem', width: '30rem' })} />
      </div>
    </td>
    <td role="cell" />
    <td role="cell" />
  </tr>
)
